/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file teamChatApi.js
 * @description
 * REST client of the internal Team Chat + Meeting module ("Csapatchat", backend
 * module id `csapatchat`). Clones the fetch + Bearer conventions of
 * {@link module:services/supportChatApi}:
 *   - Bearer token from secure storage on every call (tagged `AUTH_REQUIRED`
 *     error when signed out);
 *   - JSON bodies parsed defensively (204 / empty body tolerated);
 *   - every non-2xx surfaces as an `Error` carrying `httpStatus`, the API's
 *     `code` (e.g. `INSUFFICIENT_CREDITS`, `MEETING_NOT_CONFIGURED`) and the
 *     `balance` of a 402, so screens can branch without string-matching.
 *
 * Contract (base `${apiURL}/api/chat`, shared with the backend + admin agents):
 *   bootstrap · channels CRUD · direct / record channels · members · join/leave ·
 *   preferences · read · messages (paged, threads) · edit/delete/react/pin ·
 *   attachments (multipart `file`, max 50 MB) · search · directory · task from
 *   message · record cards · AI summarize/translate/ask · settings · meetings
 *   (start/list/get/join/heartbeat/leave/end/recording/public-link/invite/lobby) ·
 *   threads (`GET /threads`, hub Thread chip).
 *
 * Degrades gracefully: callers use {@link isChatUnavailableError} to render a
 * friendly "not available yet" state when the backend has not shipped the
 * endpoints (404/405/501), never a crash.
 */

import {Platform} from 'react-native';
import {ApiConfig} from '../config/ApiConfig';
import {getAccessToken} from '../helpers/storage';

const CHAT_URL = `${ApiConfig.apiURL}/api/chat`;

/**
 * expo-file-system, required LAZILY: this module sits in the graph of every
 * header (chat badge), and a static import drags the ESM file-system package
 * into Jest suites that never upload anything (see the appBadge/mailApi note).
 * @returns {Object|null}
 */
const loadFileSystem = () => {
    try {
        // eslint-disable-next-line global-require
        return require('expo-file-system');
    } catch (_) {
        return null;
    }
};

/** Max attachment size accepted by the backend (50 MB). */
export const CHAT_ATTACHMENT_MAX_BYTES = 50 * 1024 * 1024;

/** Max OTHER participants of a direct/group conversation (8 incl. me). */
export const CHAT_DIRECT_MAX_OTHERS = 7;

/**
 * Build a meaningful Error from a non-ok response body.
 * @param {number} status HTTP status.
 * @param {string} statusText Status text fallback.
 * @param {*} body Parsed body (object or text).
 * @returns {Error & {httpStatus: number, code?: string, balance?: number, payload?: object}}
 */
const buildError = (status, statusText, body) => {
    const payload = body && typeof body === 'object' ? body : null;
    const message = payload?.message || payload?.error || (typeof body === 'string' && body.length < 300 ? body : '')
        || statusText || `Request failed with status ${status}`;
    const error = new Error(typeof message === 'string' ? message : `Request failed with status ${status}`);
    error.httpStatus = status;
    if (payload) {
        error.payload = payload;
        if (payload.code) {
            error.code = String(payload.code);
        }
        if (payload.balance !== undefined) {
            error.balance = payload.balance;
        }
    }

    return error;
};

/**
 * Parse a text body into JSON when possible.
 * @param {string} text Raw body.
 * @returns {*} Parsed JSON, the raw text, or `true` for an empty body.
 */
const parseText = (text) => {
    if (!text) {
        return true;
    }
    try {
        return JSON.parse(text);
    } catch (_error) {
        return text;
    }
};

/**
 * Parse a fetch Response defensively (tolerates 204 / empty bodies).
 * @param {Response} response
 * @returns {Promise<any>}
 */
const parseApiResponse = async (response) => {
    if (response.status === 204) {
        return true;
    }
    let text = '';
    try {
        text = await response.text();
    } catch (_error) {
        text = '';
    }
    const body = parseText(text);
    if (!response.ok) {
        throw buildError(response.status, response.statusText, body);
    }

    return body;
};

/**
 * Resolve the bearer token, throwing a tagged error when unauthenticated.
 * @returns {Promise<string>}
 */
const requireToken = async () => {
    const accessToken = await getAccessToken();
    if (!accessToken) {
        const authError = new Error('Authentication required');
        authError.code = 'AUTH_REQUIRED';
        throw authError;
    }

    return accessToken;
};

/**
 * Serialize a query object, skipping empty values.
 * @param {Object} [params]
 * @returns {string} `?a=1&b=2` or ''.
 */
const buildQuery = (params = {}) => {
    const parts = Object.entries(params || {})
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);

    return parts.length ? `?${parts.join('&')}` : '';
};

/**
 * Perform a JSON request against the chat API.
 * @param {string} path Path relative to `/api/chat` (leading slash).
 * @param {{method?: string, query?: Object, body?: Object}} [options]
 * @returns {Promise<any>}
 */
const request = async (path, {method = 'GET', query, body} = {}) => {
    const accessToken = await requireToken();
    const response = await fetch(`${CHAT_URL}${path}${buildQuery(query)}`, {
        method,
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        ...(body !== undefined ? {body: JSON.stringify(body || {})} : {}),
    });

    return parseApiResponse(response);
};

/**
 * Coerce `{items:[...]}` / bare arrays into an array.
 * @param {*} data
 * @returns {Array}
 */
const itemsOf = (data) => {
    if (Array.isArray(data?.items)) {
        return data.items;
    }

    return Array.isArray(data) ? data : [];
};

const enc = (id) => encodeURIComponent(String(id || ''));

/**
 * Authenticated JSON request against `/api/chat` — exported so feature helpers
 * (e.g. `helpers/teamChat/chatAppsApi.js`) reuse the exact same transport,
 * token handling and error shape (`httpStatus`, `code`, `payload`).
 * @type {(path: string, options?: {method?: string, query?: Object, body?: Object}) => Promise<any>}
 */
export const chatRequest = request;

/* ─────────────────────────────── Error helpers ─────────────────────────────── */

/**
 * The backend has not shipped (or does not route) the chat endpoints yet.
 * @param {*} error
 * @returns {boolean}
 */
export const isChatUnavailableError = (error) => [404, 405, 501].includes(Number(error?.httpStatus));

/**
 * The user/store may not use chat (module disabled or missing `chat.page.access`).
 * @param {*} error
 * @returns {boolean}
 */
export const isChatForbiddenError = (error) => Number(error?.httpStatus) === 403;

/**
 * Not enough credits (402 / `INSUFFICIENT_CREDITS`).
 * @param {*} error
 * @returns {boolean}
 */
export const isInsufficientCreditsError = (error) => Number(error?.httpStatus) === 402
    || String(error?.code || '').toUpperCase() === 'INSUFFICIENT_CREDITS';

/**
 * Video meetings / recording not configured on the server (503 with code).
 * @param {*} error
 * @returns {boolean}
 */
export const isMeetingNotConfiguredError = (error) => String(error?.code || '').toUpperCase() === 'MEETING_NOT_CONFIGURED';

/** @param {*} error @returns {boolean} */
export const isRecordingNotConfiguredError = (error) => String(error?.code || '').toUpperCase() === 'RECORDING_NOT_CONFIGURED';

/* ──────────────────────────────── Bootstrap ──────────────────────────────── */

/**
 * Everything the chat shell needs at once.
 * @param {string} restaurantId Active store id.
 * @returns {Promise<{channels: Array, unreadTotal: number, mentionTotal: number, me: Object, settings: Object, credits: Object, meetingConfigured: boolean, permissions: Object}>}
 */
export const getChatBootstrap = (restaurantId) => request('/bootstrap', {query: {restaurantId}});

/**
 * Every store the caller can chat in, with that store's chat unread totals
 * (the in-chat store switcher). Needs no store scope.
 * @returns {Promise<{items: Array<{restaurantId: string, name: string, enabled: boolean, unreadTotal: number, mentionTotal: number, channelCount: number}>, isPortal: boolean}>}
 */
export const listChatStores = () => request('/stores');

/**
 * Presence heartbeat — marks the caller online and returns the store's snapshot.
 * @param {string} restaurantId Active store id.
 * @returns {Promise<{available: boolean, onlineWindowMs: number, users: Array<{userId: string, online: boolean, lastSeenAt: string}>}>}
 */
export const heartbeatChatPresence = (restaurantId) => request('/presence', {method: 'POST', query: {restaurantId}, body: {}});

/* ──────────────────────────────── Channels ───────────────────────────────── */

/**
 * @param {{restaurantId: string, type?: string, archived?: boolean, search?: string, browse?: boolean}} params
 * @returns {Promise<Array>} channels
 */
export const listChatChannels = async (params = {}) => itemsOf(await request('/channels', {query: params}));

/**
 * `customer` channels additionally carry `customerId` (+ optional
 * `customerModel` / `customerName`); the backend checks the customer belongs to
 * the store and auto-adds its active portal users (400 invalid / 403 not
 * allowed / 409 conflict).
 * @param {{restaurantId: string, type: 'public'|'private'|'customer', name: string, description?: string, memberIds?: string[], customerId?: string, customerModel?: string, customerName?: string}} payload
 * @returns {Promise<Object>} created channel
 */
export const createChatChannel = (payload) => request('/channels', {method: 'POST', body: payload});

/**
 * Open (or reuse) a direct / group conversation.
 * @param {{restaurantId: string, userIds: string[]}} payload 1..7 other user ids
 * @returns {Promise<Object>} channel
 */
export const createDirectChat = (payload) => request('/direct', {method: 'POST', body: payload});

/**
 * Open (or reuse) the discussion channel of a business record.
 * @param {{restaurantId: string, kind: string, id: string}} payload
 * @returns {Promise<Object>} channel
 */
export const createRecordChannel = (payload) => request('/record-channel', {method: 'POST', body: payload});

/** @param {string} channelId @returns {Promise<Object>} channel */
export const getChatChannel = (channelId) => request(`/channels/${enc(channelId)}`);

/**
 * @param {string} channelId
 * @param {{name?: string, description?: string, topic?: string}} patch
 * @returns {Promise<Object>} channel
 */
export const updateChatChannel = (channelId, patch) => request(`/channels/${enc(channelId)}`, {method: 'PATCH', body: patch});

/** @param {string} channelId @param {boolean} archived @returns {Promise<Object>} */
export const archiveChatChannel = (channelId, archived) => request(`/channels/${enc(channelId)}/archive`, {method: 'POST', body: {archived: !!archived}});

/** @param {string} channelId @returns {Promise<Array>} members */
export const listChatMembers = async (channelId) => itemsOf(await request(`/channels/${enc(channelId)}/members`));

/** @param {string} channelId @param {string[]} userIds @returns {Promise<Object>} */
export const addChatMembers = (channelId, userIds) => request(`/channels/${enc(channelId)}/members`, {method: 'POST', body: {userIds}});

/** @param {string} channelId @param {string} userId @returns {Promise<Object>} */
export const removeChatMember = (channelId, userId) => request(`/channels/${enc(channelId)}/members/${enc(userId)}`, {method: 'DELETE'});

/** @param {string} channelId @returns {Promise<Object>} channel */
export const joinChatChannel = (channelId) => request(`/channels/${enc(channelId)}/join`, {method: 'POST', body: {}});

/** @param {string} channelId @returns {Promise<{ok: boolean}>} */
export const leaveChatChannel = (channelId) => request(`/channels/${enc(channelId)}/leave`, {method: 'POST', body: {}});

/**
 * @param {string} channelId
 * @param {{notifyLevel?: 'all'|'mentions'|'none', mutedUntil?: (string|null), starred?: boolean, hidden?: boolean}} prefs
 * @returns {Promise<Object>} channel
 */
export const updateChatPreferences = (channelId, prefs) => request(`/channels/${enc(channelId)}/preferences`, {method: 'PATCH', body: prefs});

/**
 * @param {string} channelId
 * @param {string} [messageId] newest seen message
 * @returns {Promise<{channelId: string, lastReadMessageId: string, unreadCount: number, mentionCount: number}>}
 */
export const markChatChannelRead = (channelId, messageId) => request(`/channels/${enc(channelId)}/read`, {
    method: 'POST',
    body: messageId ? {messageId} : {},
});

/* ──────────────────────────────── Messages ───────────────────────────────── */

/**
 * @param {string} channelId
 * @param {{before?: string, after?: string, around?: string, limit?: number, threadRoot?: string}} [params]
 *   `around` = load the window centred on that message (jump to message).
 * @returns {Promise<{items: Array, hasMore: boolean, hasNewer?: boolean, anchorId?: string, anchorThreadRoot?: string}>}
 *   oldest → newest. `hasNewer` is passed through only when the backend sends a
 *   boolean; `anchorId` / `anchorThreadRoot` only answer an `around` request (a
 *   backend without `around` support omits them — see `chatWindow.normalizeMessagePage`).
 */
export const listChatMessages = async (channelId, params = {}) => {
    const data = await request(`/channels/${enc(channelId)}/messages`, {query: {limit: 50, ...params}});

    return {
        items: itemsOf(data),
        hasMore: Boolean(data?.hasMore),
        ...(typeof data?.hasNewer === 'boolean' ? {hasNewer: data.hasNewer} : {}),
        ...(data?.anchorId ? {anchorId: String(data.anchorId)} : {}),
        ...(data?.anchorThreadRoot ? {anchorThreadRoot: String(data.anchorThreadRoot)} : {}),
    };
};

/**
 * Idempotent on `clientMessageId` — safe to retry after a network failure.
 * @param {string} channelId
 * @param {{text: string, clientMessageId: string, threadRootId?: string, attachments?: Array, recordRefs?: Array, mentions?: {users: string[], channel: boolean}}} payload
 * @returns {Promise<Object>} message
 */
export const sendChatMessage = (channelId, payload) => request(`/channels/${enc(channelId)}/messages`, {method: 'POST', body: payload});

/** @param {string} messageId @param {string} text @returns {Promise<Object>} message */
export const editChatMessage = (messageId, text) => request(`/messages/${enc(messageId)}`, {method: 'PATCH', body: {text}});

/** @param {string} messageId @returns {Promise<Object>} */
export const deleteChatMessage = (messageId) => request(`/messages/${enc(messageId)}`, {method: 'DELETE'});

/** Remove all link previews of my message. @param {string} messageId @returns {Promise<Object>} message with linkPreviews: [] */
export const removeLinkPreviews = (messageId) => request(`/messages/${enc(messageId)}/link-previews`, {method: 'DELETE'});

/** Toggle my reaction. @param {string} messageId @param {string} emoji @returns {Promise<Object>} message */
export const toggleChatReaction = (messageId, emoji) => request(`/messages/${enc(messageId)}/reactions`, {method: 'POST', body: {emoji}});

/** @param {string} messageId @param {boolean} pinned @returns {Promise<Object>} message */
export const pinChatMessage = (messageId, pinned) => request(`/messages/${enc(messageId)}/pin`, {method: 'POST', body: {pinned: !!pinned}});

/** @param {string} channelId @returns {Promise<Array>} pinned messages */
export const listChatPins = async (channelId) => itemsOf(await request(`/channels/${enc(channelId)}/pins`));

/**
 * @param {string} messageId thread root id
 * @returns {Promise<{root: Object|null, items: Array}>}
 */
export const getChatThread = async (messageId) => {
    const data = await request(`/messages/${enc(messageId)}/thread`);

    return {root: data?.root || null, items: itemsOf(data)};
};

/**
 * @param {string} messageId
 * @param {{title?: string, assigneeId?: string, dueDate?: string}} payload
 * @returns {Promise<{task: Object, message: Object}>}
 */
export const createTaskFromChatMessage = (messageId, payload = {}) => request(`/messages/${enc(messageId)}/task`, {method: 'POST', body: payload});

/* ─────────────────────────────── Attachments ─────────────────────────────── */

/**
 * Normalize the upload response (`{attachment}` envelope or the bare object).
 * @param {*} data
 * @returns {{documentId: string, name: string, mimeType: string, size: number, url: string, thumbnailUrl: string}}
 */
const toAttachment = (data) => {
    const raw = data?.attachment && typeof data.attachment === 'object' ? data.attachment : data;

    return {
        documentId: raw?.documentId || raw?._id || raw?.id || null,
        name: raw?.name || raw?.filename || '',
        mimeType: raw?.mimeType || raw?.contentType || '',
        size: Number(raw?.size) || 0,
        url: raw?.url || '',
        thumbnailUrl: raw?.thumbnailUrl || '',
    };
};

/**
 * Guess a MIME type from a filename (used when the picker gives none).
 * @param {string} name
 * @returns {string}
 */
export const guessMimeType = (name = '') => {
    const lower = String(name).toLowerCase();
    if (/\.png$/.test(lower)) return 'image/png';
    if (/\.(jpe?g)$/.test(lower)) return 'image/jpeg';
    if (/\.heic$/.test(lower)) return 'image/heic';
    if (/\.webp$/.test(lower)) return 'image/webp';
    if (/\.gif$/.test(lower)) return 'image/gif';
    if (/\.pdf$/.test(lower)) return 'application/pdf';
    if (/\.docx?$/.test(lower)) return 'application/msword';
    if (/\.xlsx?$/.test(lower)) return 'application/vnd.ms-excel';
    if (/\.txt$/.test(lower)) return 'text/plain';

    return 'application/octet-stream';
};

/**
 * Upload one file into a channel (multipart field `file`). The returned
 * descriptor goes into the next message's `attachments`.
 *
 * Transport (see the iOS-watchdog / Android-size notes on the work-order photo
 * uploader): on device the file is STREAMED from disk with
 * `FileSystem.uploadAsync` (zero JS heap, FOREGROUND session so a dead network
 * fails instead of retrying forever); fetch + FormData is the fallback. Web
 * always uses a Blob FormData. Photos should already be resized by
 * `preparePhotoForUpload` before reaching this function.
 *
 * @param {string} channelId
 * @param {{uri: string, name?: string, fileName?: string, mimeType?: string, type?: string, file?: Blob, size?: number}} asset
 * @returns {Promise<{documentId: string, name: string, mimeType: string, size: number, url: string, thumbnailUrl: string}>}
 */
export async function uploadChatAttachment(channelId, asset) {
    const accessToken = await requireToken();
    const uri = asset?.uri;
    if (!uri) {
        throw new Error('No file selected');
    }
    const name = asset?.name || asset?.fileName || String(uri).split('/').pop() || `attachment-${Date.now()}`;
    const mimeType = asset?.mimeType || (asset?.type && String(asset.type).includes('/') ? asset.type : '') || guessMimeType(name);
    const url = `${CHAT_URL}/channels/${enc(channelId)}/attachments`;
    const headers = {Authorization: `Bearer ${accessToken}`, Accept: 'application/json'};

    const postFormData = async () => {
        const form = new FormData();
        if (Platform.OS === 'web') {
            const blob = asset?.file || await (await fetch(uri)).blob();
            form.append('file', blob, name);
        } else {
            form.append('file', {uri, name, type: mimeType});
        }
        form.append('name', name);
        // RN/browsers build the multipart boundary — never set Content-Type here.
        const response = await fetch(url, {method: 'POST', headers, body: form});

        return toAttachment(await parseApiResponse(response));
    };

    const FileSystem = Platform.OS !== 'web' ? loadFileSystem() : null;
    if (FileSystem && typeof FileSystem.uploadAsync === 'function') {
        let result = null;
        try {
            result = await FileSystem.uploadAsync(url, uri, {
                httpMethod: 'POST',
                uploadType: FileSystem.FileSystemUploadType?.MULTIPART,
                fieldName: 'file',
                mimeType,
                parameters: {name},
                headers,
                sessionType: FileSystem.FileSystemSessionType?.FOREGROUND,
            });
        } catch (_streamError) {
            // Native stream unavailable (e.g. content:// URI) — fall back to fetch.
            return postFormData();
        }
        // uploadAsync resolves for EVERY status — map it like a Response.
        const status = Number(result?.status) || 0;
        const body = parseText(result?.body || '');
        if (status >= 200 && status < 300) {
            return toAttachment(body);
        }
        throw buildError(status, '', body);
    }

    return postFormData();
}

/* ──────────────────────────── Search & directory ─────────────────────────── */

/**
 * @param {{restaurantId: string, q: string, channelId?: string, type?: string}} params
 * @returns {Promise<{messages: Array, files: Array, people: Array, channels: Array}>}
 */
export const searchChat = async (params) => {
    const data = await request('/search', {query: params});

    return {
        messages: Array.isArray(data?.messages) ? data.messages : [],
        files: Array.isArray(data?.files) ? data.files : [],
        people: Array.isArray(data?.people) ? data.people : [],
        channels: Array.isArray(data?.channels) ? data.channels : [],
    };
};

/** @param {{restaurantId: string, search?: string}} params @returns {Promise<Array>} members */
export const getChatDirectory = async (params) => itemsOf(await request('/directory', {query: params}));

/**
 * Live status cards of referenced business records.
 * @param {{restaurantId: string, refs: Array<{kind: string, id: string}>}} payload
 * @returns {Promise<Array>} record cards
 */
export const resolveChatRecords = async (payload) => itemsOf(await request('/records/resolve', {method: 'POST', body: payload}));

/* ─────────────────────────────────── AI ──────────────────────────────────── */

/**
 * @param {string} channelId
 * @param {{threadRootId?: string, since?: string, locale?: string}} [payload]
 * @returns {Promise<{summary: string, highlights: Array, actionItems: Array}>}
 */
export const summarizeChatChannel = (channelId, payload = {}) => request(`/channels/${enc(channelId)}/ai/summarize`, {method: 'POST', body: payload});

/** @param {string} messageId @param {string} targetLocale @returns {Promise<{text: string, locale: string}>} */
export const translateChatMessage = (messageId, targetLocale) => request(`/messages/${enc(messageId)}/translate`, {method: 'POST', body: {targetLocale}});

/** @param {string} channelId @param {{question: string, locale?: string}} payload @returns {Promise<Object>} message */
export const askChatAi = (channelId, payload) => request(`/channels/${enc(channelId)}/ai/ask`, {method: 'POST', body: payload});

/* ──────────────────────────────── Settings ───────────────────────────────── */

/** @param {string} restaurantId @returns {Promise<Object>} */
export const getChatSettings = (restaurantId) => request('/settings', {query: {restaurantId}});

/** @param {Object} payload incl. restaurantId @returns {Promise<Object>} */
export const updateChatSettings = (payload) => request('/settings', {method: 'PATCH', body: payload});

/** @returns {Promise<{meetingTranscript: boolean}>} The caller's personal chat preferences (true when never saved). */
export const getChatMyPreferences = () => request('/me/preferences');

/** @param {{meetingTranscript?: boolean}} patch @returns {Promise<{meetingTranscript: boolean}>} Saved preferences (400 INVALID_PREFERENCE). */
export const updateChatMyPreferences = (patch) => request('/me/preferences', {method: 'PATCH', body: patch});

/* ──────────────────────────────── Meetings ───────────────────────────────── */

/**
 * Start a meeting (optionally in a channel) — returns the JoinInfo.
 * @param {{restaurantId: string, channelId?: string, title?: string, userIds?: string[]}} payload
 * @returns {Promise<{meeting: Object, serverUrl: string, token: string, roomName: string, identity: string, joinUrl: string, expiresAt: string}>}
 */
export const startChatMeeting = (payload) => request('/meetings', {method: 'POST', body: payload});

/** @param {{restaurantId: string, status?: string, channelId?: string}} params @returns {Promise<Array>} */
export const listChatMeetings = async (params) => itemsOf(await request('/meetings', {query: params}));

/** @param {string} meetingId @returns {Promise<Object>} meeting */
export const getChatMeeting = (meetingId) => request(`/meetings/${enc(meetingId)}`);

/** 402 when the store has no credits. @param {string} meetingId @returns {Promise<Object>} JoinInfo */
export const joinChatMeeting = (meetingId) => request(`/meetings/${enc(meetingId)}/join`, {method: 'POST', body: {}});

/** Every 60 s while connected. @param {string} meetingId @returns {Promise<{ok: boolean, exhausted: boolean, balance: number}>} */
export const heartbeatChatMeeting = (meetingId) => request(`/meetings/${enc(meetingId)}/heartbeat`, {method: 'POST', body: {}});

/** @param {string} meetingId @returns {Promise<{ok: boolean}>} */
export const leaveChatMeeting = (meetingId) => request(`/meetings/${enc(meetingId)}/leave`, {method: 'POST', body: {}});

/** End the meeting for everyone. @param {string} meetingId @returns {Promise<Object>} meeting */
export const endChatMeeting = (meetingId) => request(`/meetings/${enc(meetingId)}/end`, {method: 'POST', body: {}});

/**
 * Propose another time for a scheduled meeting (posts a `meeting.timeProposed` chat card).
 * 409 MEETING_NOT_SCHEDULED / MEETING_NO_CHANNEL / CHANNEL_ARCHIVED, 400 INVALID_SCHEDULE / SCHEDULE_IN_PAST.
 * @param {string} meetingId
 * @param {{start: string, end: string, note?: string}} payload ISO instants + optional note.
 * @returns {Promise<{message: Object}>}
 */
export const proposeChatMeetingTime = (meetingId, {start, end, note} = {}) => request(`/meetings/${enc(meetingId)}/propose-time`, {
    method: 'POST',
    body: {start, end, ...(note ? {note} : {})},
});

/**
 * Move a scheduled meeting (organizer only).
 * @param {string} meetingId
 * @param {{start: string, end?: string}} payload ISO instants.
 * @returns {Promise<Object>}
 */
export const rescheduleChatMeeting = (meetingId, {start, end} = {}) => request(`/meetings/${enc(meetingId)}/schedule`, {
    method: 'PUT',
    body: {start, ...(end ? {end} : {})},
});

/**
 * Cancel a scheduled meeting (organizer only).
 * @param {string} meetingId
 * @returns {Promise<Object>}
 */
export const cancelScheduledChatMeeting = (meetingId) => request(`/meetings/${enc(meetingId)}/schedule`, {method: 'DELETE'});

/** @param {string} meetingId @param {boolean} enabled @returns {Promise<Object>} meeting */
export const setChatMeetingRecording = (meetingId, enabled) => request(`/meetings/${enc(meetingId)}/recording`, {method: 'POST', body: {enabled: !!enabled}});

/* ───────────────────── Hub: public link · invite · lobby · threads ───────────────────── */

/**
 * Turn the meeting's public (guest) link on/off.
 * @param {string} meetingId
 * @param {boolean} enabled
 * @returns {Promise<Object>} meeting incl. `publicUrl` + `publicLinkEnabled`
 */
export const setChatMeetingPublicLink = (meetingId, enabled) => request(`/meetings/${enc(meetingId)}/public-link`, {method: 'POST', body: {enabled: !!enabled}});

/**
 * Invite colleagues (push + chat) and/or external people (e-mail) to a meeting.
 * @param {string} meetingId
 * @param {{userIds?: string[], emails?: string[], message?: string}} payload
 * @returns {Promise<Object>}
 */
export const inviteToChatMeeting = (meetingId, {userIds = [], emails = [], message} = {}) => request(`/meetings/${enc(meetingId)}/invite`, {
    method: 'POST',
    body: {userIds, emails, ...(message ? {message} : {})},
});

/**
 * Guests waiting in the meeting lobby.
 * @param {string} meetingId
 * @returns {Promise<*>} `{items:[guest]}` (see chatHub.mapLobbyGuests)
 */
export const listChatMeetingLobby = (meetingId) => request(`/meetings/${enc(meetingId)}/lobby`);

/**
 * Admit or deny a lobby guest.
 * @param {string} meetingId
 * @param {string} guestId
 * @param {boolean} admit
 * @returns {Promise<Object>}
 */
export const respondChatMeetingLobby = (meetingId, guestId, admit) => request(`/meetings/${enc(meetingId)}/lobby/${enc(guestId)}`, {
    method: 'POST',
    body: {admit: !!admit},
});

/**
 * Threads I take part in (newest reply first) — see chatHub.mapThreadItems.
 * @param {{restaurantId: string, unread?: boolean}} params
 * @returns {Promise<*>} `{items:[{root, channel:{_id,name,type}, replyCount, lastReplyAt, unreadCount}]}`
 */
export const listChatThreads = ({restaurantId, unread} = {}) => request('/threads', {
    query: {restaurantId, ...(unread ? {unread: 'true'} : {})},
});
